import { useEffect, useRef, useState } from "react";
import ENDPOINTS from "@/configs/endpoints";
import { useAuthStore } from "@/store/useAuthStore";
import { useSupervisorMessageStore } from "@/store/useSupervisorMessageStore";
import { ChatMessage } from "@/configs/@types/socket";
import { Socket_Events } from "@/utils/enums";
import { useNotificationStore } from "@/store/useNotificationStore";
import notificationSoundUrl from "@/assets/sounds/notification.mp3";


export function useSupervisorSocket() {
  const token = useAuthStore((state) => state.token);
  const socketSessionId = useAuthStore((state) => state.socketSessionId);
  const socketRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const addMessage = useSupervisorMessageStore((s) => s.addMessage);
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(notificationSoundUrl);
    audio.preload = "auto";
    audio.volume = 0.5;
    notificationAudioRef.current = audio;
  }, []);

  const playNotificationSound = () => {
    try {
      let audio = notificationAudioRef.current;
      if (!audio) {
        audio = new Audio(notificationSoundUrl);
        audio.volume = 0.5;
      }
      audio.currentTime = 0;
      audio.play().catch((e) => {
        console.warn("Unable to play notification sound", e);
      });
    } catch (err) {
      console.warn("Error playing notification sound", err);
    }
  };

  useEffect(() => {
    if (!token) return;

    const scheduleReconnect = () => {
      if (reconnectAttemptsRef.current >= 5) {
        console.warn("[SupervisorWS] Max reconnect attempts reached");
        return;
      }
      const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
      reconnectAttemptsRef.current += 1;
      console.log(`[SupervisorWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
      reconnectTimerRef.current = window.setTimeout(connect, delay);
    };

    function connect() {
      if (
        socketRef.current &&
        (socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const wsUrl = `${ENDPOINTS.WS.CONNECT}?token=${token}&sessionId=${socketSessionId || ""}`;
      console.log("[SupervisorWS] Connecting:", wsUrl);
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        console.log("[SupervisorWS] Connected");
        setConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onclose = (e) => {
        console.log(`[SupervisorWS] Disconnected (code: ${e.code})`);
        setConnected(false);
        if (e.code !== 1000) scheduleReconnect();
      };

      ws.onerror = (err) => {
        console.error("[SupervisorWS] Error", err);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const requestType = data.requestType;
          const chatId = data.chatId;

          if (!chatId && requestType !== Socket_Events.USER_DELETED) return;

          // Handle user deleted event
          if (requestType === Socket_Events.USER_DELETED) {
            console.log("[SupervisorWS] User deleted event received, logging out...");
            useAuthStore.getState().logout().catch((err) => console.error("Auto-logout failed", err));
            return;
          }

          const handleUnread = (cId: string) => {
            const storeState = useSupervisorMessageStore.getState();
            if (storeState.activeChatId !== cId) {
              storeState.incrementUnreadCount(cId);
            }
          };

          // Agent message forwarded to supervisor
          if (requestType === "MESSAGE_TO_SUPERVISOR") {
            const msg: ChatMessage = {
              id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
              text: data.text || "",
              sender: "agent",
              timestamp: data.ts || Date.now(),
              userId: data.userId,
              chatId,
              isMe: false,
            };
            addMessage(chatId, msg);
            handleUnread(chatId);
          }

          // User message
          if (requestType === "USER_MESSAGE") {
            const msg: ChatMessage = {
              id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
              text: data.text || "",
              sender: "user",
              timestamp: data.ts || Date.now(),
              userId: data.userId,
              chatId,
              isMe: false,
            };
            addMessage(chatId, msg);
            handleUnread(chatId);
          }

          // Customer left chat
          if (requestType === Socket_Events.CUSTOMER_LEFT_CHAT) {
            console.log(`[SupervisorWS] Customer left chat: ${chatId}`);
            useSupervisorMessageStore.getState().setRemovedChatId(chatId);
          }

          if (requestType === Socket_Events.AGENT_LEFT_CHAT) {
            console.log(`[SupervisorWS] Agent left chat: ${chatId}`);
            useSupervisorMessageStore.getState().setRemovedChatId(chatId);
          }

          // // New chat request
          if (requestType === Socket_Events.SUPERVISOR_LIVE_CHAT_NOTIFICATION) {
            console.log("[SupervisorWS] New chat request:", chatId);
            const newChat = {
              chatId: chatId,
              customerName: data.customerName || "Anonymous User",
              queueName: data.queueName || "General Inquiry",
              agentName: data.agentName || "System Assigned",
            };
            useSupervisorMessageStore.getState().setNewChat(newChat);
          }

          // Handle ticket notifications
          if (requestType === Socket_Events.NEW_TICKET_NOTIFICATION) {
            useNotificationStore.getState().addNotification({
              title: "New Ticket Created",
              description: `A new ticket ${data.ticketId || ""} has been created.`,
              type: "ticket",
              metadata: data
            });
            playNotificationSound();
            useNotificationStore.getState().incrementTicketEventCount();
          }

          if (requestType === Socket_Events.TICKET_ASSIGNED_TO_AGENT) {
            useNotificationStore.getState().addNotification({
              title: "Ticket Assigned",
              description: `Ticket ${data.ticketId || ""} has been assigned to you.`,
              type: "ticket",
              metadata: data
            });
            playNotificationSound();
            useNotificationStore.getState().incrementTicketEventCount();
          }

          if (requestType === Socket_Events.ESCALATE_TO_SUPERVISOR) {
            useNotificationStore.getState().addNotification({
              title: "Ticket Escalated",
              description: `Ticket ${data.ticketId || ""} has been escalated to supervisor.`,
              type: "ticket",
              metadata: data
            });
            playNotificationSound();
            useNotificationStore.getState().incrementTicketEventCount();
          }

          if (requestType === Socket_Events.TICKET_REOPEN_NOTIFICATION) {
            useNotificationStore.getState().addNotification({
              title: "Ticket Reopened",
              description: `Ticket ${data.ticketId || ""} has been reopened.`,
              type: "ticket",
              metadata: data
            });
            playNotificationSound();
            useNotificationStore.getState().incrementTicketEventCount();
          }

          if (requestType === Socket_Events.TICKET_RESOLUTION_NOTIFICATION) {
            useNotificationStore.getState().addNotification({
              title: "Ticket Resolved",
              description: `Ticket ${data.ticketId || ""} has been resolved.`,
              type: "ticket",
              metadata: data
            });
            playNotificationSound();
            useNotificationStore.getState().incrementTicketEventCount();
          }


        } catch (err) {
          console.error("[SupervisorWS] Failed to parse message", err);
        }
      };
    }

    connect();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.close();
        socketRef.current = null;
      }
      setConnected(false);
    };
  }, [token]);

  return { connected };
}
